import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Shield, Star, MessageSquare, TrendingUp, CheckCircle, Users, Clock, Award, Briefcase } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Footer from '../components/Footer';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

// Default content (fallback if CMS content not set)
const defaultContent = {
  hero: {
    content: `
      <h2>Find Skilled UK Tradespeople in Minutes</h2>
      <p>Connect with qualified carpenters, decorators, plasterers, and more. Profiles with clear verification status, honest reviews, and direct contact—no agency fees.</p>
    `
  },
  features: {
    content: `
      <div class="feature">
        <h3>Clearer Profile Information</h3>
        <p>Profiles can include credentials, work history, photos and employer reviews. Where OnSiteHand has checked submitted evidence, its verification status is shown clearly.</p>
      </div>
      <div class="feature">
        <h3>Direct Contact</h3>
        <p>Message tradespeople directly. No middleman, no agency fees, just straightforward hiring.</p>
      </div>
      <div class="feature">
        <h3>Honest Reviews</h3>
        <p>Read genuine feedback from employers who have hired the tradesperson you are considering.</p>
      </div>
    `
  },
  how_it_works: {
    content: `
      <div class="step">
        <h3>1. Search</h3>
        <p>Browse tradespeople by trade, location, and availability.</p>
      </div>
      <div class="step">
        <h3>2. Review</h3>
        <p>Check profiles, ratings, and the status of any submitted credentials.</p>
      </div>
      <div class="step">
        <h3>3. Contact</h3>
        <p>Message directly and arrange the work.</p>
      </div>
    `
  },
  cta: {
    content: `
      <h2>Ready to Find Your Next Tradesperson?</h2>
      <p>Join thousands of employers finding skilled tradespeople every day.</p>
    `
  }
};

export default function Landing() {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  const [cmsContent, setCmsContent] = useState(null);

  useEffect(() => {
    fetchCMSContent();
  }, []);

  const fetchCMSContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages/landing`);
      if (response.data.sections) {
        setCmsContent(response.data.sections);
      }
    } catch (error) {
      // Fallback to defaults
    }
  };

  // Helper to get content with fallback
  const getContent = (sectionId) => {
    if (cmsContent && cmsContent[sectionId]?.content) {
      return cmsContent[sectionId].content;
    }
    return defaultContent[sectionId]?.content || '';
  };

  // Helper to render HTML safely using DOMPurify to prevent XSS attacks
  const renderHTML = (html) => {
    return { __html: DOMPurify.sanitize(html, { 
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'span', 'a', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'br', 'img'],
      ALLOWED_ATTR: ['href', 'target', 'class', 'id', 'src', 'alt', 'title'],
      ALLOW_DATA_ATTR: false
    })};
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink-0">
            <img 
              src="/icons/onsitehand-icon.png" 
              alt="OnsiteHand" 
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex-shrink-0"
            />
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 whitespace-nowrap" data-testid="site-title">OnsiteHand</h1>
          </div>
          <nav className="hidden lg:flex items-center gap-6">
            <button 
              onClick={() => navigate('/register?type=employer')}
              className="text-slate-600 hover:text-slate-900 font-medium whitespace-nowrap"
            >
              For Employers
            </button>
            <button 
              onClick={() => navigate('/register?type=tradesperson')}
              className="text-slate-600 hover:text-slate-900 font-medium whitespace-nowrap"
            >
              For Tradespeople
            </button>
            <button 
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="text-slate-600 hover:text-slate-900 font-medium whitespace-nowrap"
            >
              How It Works
            </button>
          </nav>
          <div className="flex gap-2 sm:gap-3 flex-shrink-0">
            <button 
              onClick={() => navigate('/login')}
              className="btn-secondary text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 whitespace-nowrap"
              data-testid="header-login-btn"
            >
              Sign In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="btn-accent text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 whitespace-nowrap"
              data-testid="header-register-btn"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Split Entry */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden py-12 sm:py-16 lg:py-20 px-4" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="max-w-5xl mx-auto relative">
          {/* Main Headline */}
          <div className="text-center mb-10 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight" data-testid="hero-title">
              {get('landing.hero.title', 'Find available tradespeople, subcontractors and trade teams when you need extra hands.')}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto" data-testid="hero-subtitle">
              {get('landing.hero.subtitle', 'Search by trade, location and availability. View profiles, message tradespeople and make contact directly.')}
            </p>
          </div>

          {/* Split Entry Question */}
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-lg sm:text-xl text-slate-400 font-medium">
              {get('landing.hero.question', 'What are you here to do?')}
            </p>
          </div>

          {/* Two Entry Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {/* Card 1: Find Trades (Employers) */}
            <button
              onClick={() => navigate('/search')}
              className="group relative bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-orange-500/30 focus:outline-none focus:ring-4 focus:ring-orange-500/50"
              data-testid="find-trades-btn"
            >
              {/* Icon */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform">
                <Search className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              
              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {get('landing.hero.cta_find_trades', 'Find Trades')}
              </h2>
              
              {/* Description */}
              <p className="text-orange-100 text-sm sm:text-base mb-4">
                {get('landing.hero.cta_find_trades_desc', 'Search by trade, location, availability and worker type. Find individuals, subcontractors and small trade teams.')}
              </p>
              
              {/* Arrow indicator */}
              <div className="flex items-center text-white/80 group-hover:text-white text-sm font-medium">
                <span>Start searching</span>
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>

              {/* Subtle corner badge */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-xs text-white font-medium">For Employers</span>
              </div>
            </button>

            {/* Card 2: Get Work (Tradespeople) */}
            <button
              onClick={() => navigate('/register?type=tradesperson')}
              className="group relative bg-gradient-to-br from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 border border-slate-600 hover:border-slate-500 rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl hover:shadow-slate-500/20 focus:outline-none focus:ring-4 focus:ring-slate-500/50"
              data-testid="get-work-btn"
            >
              {/* Icon */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform">
                <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 text-orange-400" />
              </div>
              
              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {get('landing.hero.cta_get_work', 'Join as a Trade')}
              </h2>
              
              {/* Description */}
              <p className="text-slate-300 text-sm sm:text-base mb-4">
                {get('landing.hero.cta_get_work_desc', 'Create your profile and set your availability. Employers find you when they need extra hands.')}
              </p>
              
              {/* Arrow indicator */}
              <div className="flex items-center text-slate-400 group-hover:text-white text-sm font-medium">
                <span>Create profile</span>
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>

              {/* Subtle corner badge */}
              <div className="absolute top-4 right-4 bg-orange-500/20 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-xs text-orange-300 font-medium">For Tradespeople</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Trust Strip - Immediately after hero */}
      <section className="bg-white border-y border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Trust Point 1 */}
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-semibold text-sm sm:text-base">{get('landing.trust.point_1', 'Verification status shown clearly')}</p>
              </div>
            </div>

            {/* Trust Point 2 */}
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-semibold text-sm sm:text-base">{get('landing.trust.point_2', 'Employer reviews where available')}</p>
              </div>
            </div>

            {/* Trust Point 3 */}
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-slate-900 font-semibold text-sm sm:text-base">{get('landing.trust.point_3', 'No agencies. Direct contact only.')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free marketplace banner — single factual line, location-neutral. */}
      <section className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 py-4 px-4" data-testid="free-browsing-banner">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-emerald-800 font-semibold text-sm sm:text-base flex items-center justify-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
            <span>{get('landing.free_browsing.text', 'OnSiteHand is free to use for tradespeople and employers.')}</span>
          </p>
        </div>
      </section>

      {/* Features Section - CMS Editable */}
      <section className="py-20 px-4" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">{get('landing.features.badge', 'Why Choose Us')}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 font-heading uppercase tracking-tight">
              {get('landing.features.title', 'Built for the Trade')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {get('landing.features.subtitle', 'The smarter way to find and hire skilled tradespeople in the UK')}
            </p>
          </div>
          
          {cmsContent?.features?.content ? (
            <div 
              dangerouslySetInnerHTML={renderHTML(getContent('features'))}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 cms-features [&_.feature]:bg-white [&_.feature]:rounded-xl [&_.feature]:p-8 [&_.feature]:shadow-sm [&_.feature]:border [&_.feature]:border-slate-200 [&_.feature]:hover:shadow-md [&_.feature]:transition-shadow [&_.feature_h3]:text-xl [&_.feature_h3]:font-bold [&_.feature_h3]:text-slate-900 [&_.feature_h3]:mb-3 [&_.feature_p]:text-slate-600"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Feature 1 - Large */}
              <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-8 text-white group hover:shadow-xl hover:shadow-slate-900/20 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3 font-heading uppercase tracking-wide">{get('landing.features.verified_title', 'Clearer Profile Information')}</h3>
                    <p className="text-slate-300 text-lg leading-relaxed">{get('landing.features.verified_desc', 'Profiles can include credentials, work history, photos and employer reviews. Where OnSiteHand has checked submitted evidence, its verification status is shown clearly.')}</p>
                  </div>
                </div>
              </div>
              
              {/* Feature 2 */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 group hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-300">
                  <MessageSquare className="h-6 w-6 text-orange-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">{get('landing.features.direct_title', 'Direct Contact')}</h3>
                <p className="text-slate-600">{get('landing.features.direct_desc', 'Message tradespeople directly. No middleman, no agency fees, just straightforward hiring.')}</p>
              </div>
              
              {/* Feature 3 */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 group hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-300">
                  <Star className="h-6 w-6 text-orange-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">{get('landing.features.reviews_title', 'Honest Reviews')}</h3>
                <p className="text-slate-600">{get('landing.features.reviews_desc', 'Read genuine feedback from employers who have hired the tradesperson you\'re considering.')}</p>
              </div>
              
              {/* Feature 4 */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 group hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-300">
                  <Clock className="h-6 w-6 text-orange-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">{get('landing.features.fast_title', 'Fast Response')}</h3>
                <p className="text-slate-600">{get('landing.features.fast_desc', 'Contact tradespeople directly through real-time messaging and arrange the work between yourselves.')}</p>
              </div>
              
              {/* Feature 5 - Large */}
              <div className="lg:col-span-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-8 text-white group hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3 font-heading uppercase tracking-wide">{get('landing.features.grow_title', 'Grow Your Business')}</h3>
                    <p className="text-orange-100 text-lg leading-relaxed">{get('landing.features.grow_desc', 'Tradespeople: showcase your skills, build your reputation, and connect with quality clients. Take control of your career.')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works - CMS Editable */}
      <section id="how-it-works" className="py-20 px-4 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-slate-200 text-slate-700 text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">Simple Process</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 font-heading uppercase tracking-tight">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Finding the right tradesperson is easy with OnsiteHand
            </p>
          </div>
          
          {cmsContent?.how_it_works?.content ? (
            <div 
              dangerouslySetInnerHTML={renderHTML(getContent('how_it_works'))}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 cms-steps"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-200" style={{top: '4rem'}}></div>
              
              <div className="text-center relative group">
                <div className="w-16 h-16 rounded-full bg-orange-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-orange-500/30 transition-all duration-300 relative z-10">
                  1
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">Search</h3>
                <p className="text-slate-600">Browse tradespeople by trade, location, and availability. Filter by ratings and credentials.</p>
              </div>
              <div className="text-center relative group">
                <div className="w-16 h-16 rounded-full bg-orange-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-orange-500/30 transition-all duration-300 relative z-10">
                  2
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">Review</h3>
                <p className="text-slate-600">Check profiles, ratings, work photos and the status of any submitted credentials before making contact.</p>
              </div>
              <div className="text-center relative group">
                <div className="w-16 h-16 rounded-full bg-orange-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-orange-500/30 transition-all duration-300 relative z-10">
                  3
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading uppercase tracking-wide">Contact</h3>
                <p className="text-slate-600">Message your chosen tradesperson directly and arrange the work on your terms.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How OnsiteHand Helps — Real-world examples */}
      <section className="py-20 px-4" data-testid="how-onsitehand-helps-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">Real Situations</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 font-heading uppercase tracking-tight">
              How OnsiteHand Helps
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Built for real situations employers and tradespeople deal with all the time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Card 1 — Employer: Cover */}
            <div className="relative bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group" data-testid="help-card-cover">
              <div className="absolute top-6 right-6 text-xs font-medium text-slate-400 bg-slate-100 rounded-full px-2.5 py-0.5">Employer</div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-orange-500 transition-colors duration-300">
                <Clock className="h-5 w-5 text-orange-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug pr-16">
                "One of your lads is off and the job still needs covering."
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                See who has recently marked themselves available. No ringing round, no waiting for call-backs. See availability, trade and rate before you reach out.
              </p>
            </div>

            {/* Card 2 — Employer: Weekend */}
            <div className="relative bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group" data-testid="help-card-labour">
              <div className="absolute top-6 right-6 text-xs font-medium text-slate-400 bg-slate-100 rounded-full px-2.5 py-0.5">Employer</div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-orange-500 transition-colors duration-300">
                <Users className="h-5 w-5 text-orange-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug pr-16">
                "You need weekend help to keep a project moving."
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Filter by availability, worker type and location. Find a subcontractor or small team for short-term support without agency fees.
              </p>
            </div>

            {/* Card 3 — Tradesperson: Gaps */}
            <div className="relative bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group" data-testid="help-card-tradesperson">
              <div className="absolute top-6 right-6 text-xs font-medium text-orange-500 bg-orange-50 rounded-full px-2.5 py-0.5">Tradesperson</div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-orange-500 transition-colors duration-300">
                <Briefcase className="h-5 w-5 text-orange-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug pr-16">
                "You're a trade with gaps in the diary and want employers to find you."
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Free to create a profile. Set when you're available. Show your trade, rate and work area. Employers can find you when they need extra hands.
              </p>
            </div>

            {/* Card 4 — Employer: Subcontractor */}
            <div className="relative bg-white rounded-xl border border-slate-200 p-6 sm:p-8 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group" data-testid="help-card-extra-work">
              <div className="absolute top-6 right-6 text-xs font-medium text-slate-400 bg-slate-100 rounded-full px-2.5 py-0.5">Employer</div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-5 group-hover:bg-orange-500 transition-colors duration-300">
                <TrendingUp className="h-5 w-5 text-orange-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug pr-16">
                "You need a subcontractor or small team for short-term support."
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Browse, view full profiles and message tradespeople for free. Use availability filters to prioritise people who have marked themselves available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example Stories — Placeholder testimonials */}
      <section className="py-16 px-4 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 bg-slate-200 text-slate-700 text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">Example Stories</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              What it looks like in practice
            </h2>
            <p className="text-sm text-slate-500">Example scenarios based on how OnSiteHand is designed to work.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="testimonial-1">
              <p className="text-slate-700 italic mb-4 leading-relaxed">
                "Needed cover at short notice after one of our lads went on holiday. OnSiteHand made it simple to see who was available without ringing round."
              </p>
              <p className="text-sm text-slate-500">— Example employer story</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="testimonial-2">
              <p className="text-slate-700 italic mb-4 leading-relaxed">
                "I had gaps in my diary mid-week and set myself as available. Within a couple of days an employer contacted me for a 3-day job."
              </p>
              <p className="text-sm text-slate-500">— Example tradesperson story</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Employer Focused */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-orange-500 to-orange-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Need a tradesperson today?
          </h2>
          <p className="text-lg text-orange-100 mb-8 max-w-2xl mx-auto">
            Only showing trades who are actually available. No chasing. No waiting. Just book.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            {/* Primary CTA - Large & Dominant */}
            <div className="flex flex-col items-center">
              <button 
                onClick={() => navigate('/search')}
                className="bg-white text-orange-600 px-10 py-5 rounded-xl font-bold text-lg sm:text-xl hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform"
                data-testid="cta-employer-btn"
              >
                Check availability near you
              </button>
              <span className="text-orange-200 text-sm mt-2">Takes 10 seconds</span>
            </div>
            
            {/* Secondary CTA - Smaller & Less Prominent */}
            <button 
              onClick={() => navigate('/register?type=tradesperson')}
              className="text-white/90 hover:text-white px-5 py-2.5 rounded-lg font-medium text-sm border border-white/30 hover:border-white/50 hover:bg-white/10 transition-all"
              data-testid="cta-tradesperson-btn"
            >
              I'm a tradesperson
            </button>
          </div>
          
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
